<template>
  <div
    ref="buttonContainer"
    class="button-item -has-button"
    :class="[
      { '-is-selected': selected, 'button-item_light': light },
      $attrs.class
    ]"
    :style="styleAttrs.style"
    @contextmenu.prevent="onContextMenu"
  >
    <button
      v-bind="functionalAttrs"
      :type="type"
      class="button-item__button"
      @click="$emit('click', $event)"
    >
      <span class="button-item__text">
        <slot></slot>
      </span>
      <slot name="after-text"></slot>
    </button>
    <div
      v-if="icons.length > 0 || $slots.icons != null"
      class="button-item__buttons"
    >
      <button
        v-for="icon in icons"
        :key="icon"
        type="button"
        class="button-icon button-item__icon"
        @click="onIconClick($event, icon)"
      >
        <lucide-icon :name="icon" />
      </button>
      <slot name="icons"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from "vue";
import { useAttrsAndStyle } from "@core/composables/attrsAndStyle";
import LucideIcon from "@core/ui/LucideIcon.vue";
import type { Handleable } from "@core/types/Events";

const acceptedIcons = ["trash", "x", "trash-2", "info"] as const;
type IconName = (typeof acceptedIcons)[number];

defineOptions({
  inheritAttrs: false
});

withDefaults(defineProps<{
  icons?: IconName[];
  selected?: boolean;
  light?: boolean;
  type?: "button" | "submit";
}>(), {
  icons: () => [],
  selected: false,
  light: false,
  type: "button"
});

type IconClickEventType = `click:${IconName}`;

const emit = defineEmits<{
  (e: "contextmenu", event: MouseEvent): void;
  (e: "click", event: MouseEvent): void;
  (e: IconClickEventType, event: MouseEvent): void;
}>();

const buttonContainer = useTemplateRef("buttonContainer");
const { styleAttrs, functionalAttrs } = useAttrsAndStyle();

function onIconClick(mouseEvent: MouseEvent & Handleable, icon: IconName) {
  mouseEvent.hasBeenHandled = true;
  emit(`click:${icon}`, mouseEvent);
}
function onContextMenu(event: MouseEvent) {
  emit("contextmenu", event);
}

defineExpose({
  el: buttonContainer,
  getBoundingClientRect() {
    return buttonContainer.value?.getBoundingClientRect() ?? new DOMRect();
  }
});
</script>

<style lang="scss">
.button-item {
  position: relative;
  z-index: 1;
  --background-color: var(--color-background-close);

  display: flex;
  display: grid;
  grid-template-columns: minmax(0, 1fr) min-content;
  align-items: stretch;
  width: 100%;
  max-width: 20rem;
  min-height: 2.1em;
  padding: 0;
  min-width: 0;

  text-align: left;
  font-size: 1rem;
  font-family: var(--font-family-ui);

  outline: none;
  border-radius: 5px;
  border: 1px solid var(--background-color);
  background-color: var(--background-color);
  box-shadow:
    0 1px 5px rgba(0, 0, 0, 0.3),
    inset 0 0 0 0 var(--color-shadow-2);
  transition:
    box-shadow 200ms,
    border-color 200ms;

  &:where(:not(.-has-button)),
  &__button {
    padding: 0.25em 0.75rem;
  }

  &__button {
    display: flex;
    appearance: none;
    border: none;
    outline: none;
    background-color: transparent;
    font-size: 1em;
    font-family: var(--font-family-ui);
    width: 100%;
    text-align: inherit;
    overflow: hidden;
    min-width: 1rem;
    height: max-content;
    margin-left: -1px;
    margin-top: -1px;
    margin-bottom: -1px;
    align-items: center;

    &:focus-visible {
      outline: none;
    }
  }

  &__text {
    overflow: hidden;
    hyphens: auto;
    text-overflow: ellipsis;
    width: 100%;
    padding: 0.25em 0; // needs vertical padding to prevent letter cutoff (e.g. letter "g")
    min-height: 1.15em; // prevents shift when adding first letter
    min-width: 0.5rem;
    display: inline-block;
  }

  &_toggle {
    justify-content: center;
    width: auto;
    text-align: center;
  }
  &_toggle &__text {
    text-align: center;
  }

  &__buttons {
    display: flex;
    margin-right: -1px;
    margin-top: -1px;
    margin-bottom: -1px;
    flex-shrink: 0;
    padding: 0;
  }

  &__icon {
    position: relative;
    color: var(--color-text-lighter);
    border-radius: 0;
    font-size: 15px;
    padding: 0.25rem 0.5rem;
    transition: color 200ms, background-color 200ms;
    &:hover {
      background-color: rgba(var(--tint-contrast), 0.01);
    }
    &:active {
      background-color: rgba(var(--tint-contrast), 0.025);
    }
  }
  &__icon-darker {
    color: var(--color-text-light);
  }

  &:hover {
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
    --background-color: var(--color-background-close-hover);
  }
  &:is(button):active,
  &:has(> .button-item__button:active){
    transition: box-shadow 100ms;
    border-color: var(--color-text-lighter);
    box-shadow:
      0 0px 0px rgba(0, 0, 0, 0.4),
      inset 0 0px 4px var(--color-shadow-2);
  }
  &:focus-visible,
  &:has(.button-item__button:focus-visible) {
    border-color: var(--color-action);
  }

  &_light {
    border: 1px solid var(--color-border-light);
    font-weight: 600;
    box-shadow: none;
    &:hover {
      box-shadow: none;
    }
  }

  &.-is-selected {
    border-color: var(--color-action);
    box-shadow:
      0 0 5px rgba(0, 0, 0, 0.3),
      inset 0 0 0 0 var(--color-shadow-2);
    outline: 1px solid var(--color-action);
    //border-color: var(--color-action);
  }
}
</style>
